import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { TrendingUp, Loader2, FileText, RotateCcw } from "lucide-react";
import aapi, { inr, formatApiError } from "./aapi";
import { PageHeader, inputCls, selectCls } from "@/panel/components";
import { EASE } from "@/components/Motion";

const FIELDS = [
  ["collections", "Gross collections (₹)"],
  ["refunds_total", "Refunds issued (₹)"],
  ["wallet_held", "Wallet balances held (₹)"],
  ["orders_total", "Total orders"],
  ["orders_value", "Total order value (₹)"],
  ["completed", "Completed orders"],
  ["active", "In-progress / pending orders"],
  ["cancelled", "Cancelled / refunded orders"],
  ["cancelled_value", "Cancelled order value (₹)"],
  ["users_total", "Registered customers"],
  ["unique_customers", "Transacting customers"],
  ["new_customers", "New customers (period)"],
  ["aov", "Average order value (₹)"],
  ["prev_collections", "Previous period collections (₹)"],
  ["prev_orders_total", "Previous period orders"],
  ["cost_pct", "Estimated service cost %"],
];

const num = (v) => parseFloat(v) || 0;

export default function AdminRevenue() {
  const [days, setDays] = useState("0");
  const [fig, setFig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [inc, setInc] = useState({ methods: true, services: true, customers: true });

  const load = async (d = days) => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await aapi.get(`/reports/revenue-data?days=${d}`);
      const f = {};
      FIELDS.forEach(([k]) => { f[k] = data[k] ?? ""; });
      setFig(f);
      toast.success("System figures loaded — ab koi bhi figure edit kar sakte ho");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const net = fig ? num(fig.collections) - num(fig.refunds_total) : 0;
  const profit = fig ? net * (1 - num(fig.cost_pct) / 100) : 0;
  const margin = net ? (profit / net) * 100 : 0;
  const completion = fig && num(fig.orders_total) ? (num(fig.completed) / num(fig.orders_total)) * 100 : 0;
  const growth = fig && num(fig.prev_collections) ? ((net - num(fig.prev_collections)) / num(fig.prev_collections)) * 100 : null;

  const generate = async () => {
    setBusy(true);
    setResult(null);
    try {
      const figures = {};
      FIELDS.forEach(([k]) => { if (fig[k] !== "") figures[k] = fig[k]; });
      const { data } = await aapi.post("/reports/revenue.pdf", {
        days: parseInt(days, 10), figures,
        include_methods: inc.methods, include_top_services: inc.services, include_top_customers: inc.customers,
      });
      setResult(data);
      toast.success(`Revenue statement ${data.doc_id} generated`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggle = (key, label, tid) => (
    <label className="flex cursor-pointer items-center gap-2">
      <input type="checkbox" checked={inc[key]} onChange={(e) => setInc({ ...inc, [key]: e.target.checked })}
        className="h-4 w-4 accent-gold" data-testid={tid} />
      <span className="text-[11px] text-neutral-400">{label}</span>
    </label>
  );

  return (
    <div data-testid="admin-revenue-page">
      <PageHeader title="Revenue Statement" sub="Bank-grade statement of revenue — system figures auto-load hote hain, har figure editable hai, phir QR-verified PDF banao." />

      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
        className="max-w-3xl rounded-3xl border border-gold/20 bg-panel/80 p-8">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-gold/30 bg-gold/10">
            <TrendingUp size={19} className="text-gold" />
          </div>
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] text-gold/70">STATEMENT OF REVENUE &amp; OPERATIONS</div>
            <p className="mt-1 text-xs text-neutral-500">Total orders, cancelled orders, gross profit, customer metrics, period comparison — sab kuch included.</p>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Period</label>
            <select value={days} onChange={(e) => setDays(e.target.value)} className={`${selectCls} mt-2`} data-testid="revenue-period-select">
              <option value="0">All time (inception to date)</option>
              <option value="7">Last 7 days</option>
              <option value="15">Last 15 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last 365 days</option>
            </select>
          </div>
          <button onClick={() => load()} disabled={loading} data-testid="revenue-load-btn"
            className="flex items-center gap-2 rounded-xl border border-gold/40 px-5 py-3 text-xs font-bold text-goldlight transition-colors hover:bg-gold/10 disabled:opacity-40">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
            LOAD SYSTEM FIGURES
          </button>
        </div>

        {fig && (
          <>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="revenue-derived">
              {[
                ["NET COLLECTIONS", inr(net), "revenue-net"],
                ["GROSS PROFIT (EST.)", inr(profit), "revenue-profit"],
                ["GROSS MARGIN", `${margin.toFixed(1)}%`, "revenue-margin"],
                ["FULFILMENT RATE", `${completion.toFixed(1)}%`, "revenue-completion"],
              ].map(([label, val, tid]) => (
                <div key={tid} className="rounded-xl border border-gold/25 bg-gold/[0.06] px-4 py-3">
                  <div className="font-mono text-[8px] uppercase tracking-[0.18em] text-neutral-500">{label}</div>
                  <div className="mt-1 text-sm font-bold text-goldlight" data-testid={tid}>{val}</div>
                </div>
              ))}
            </div>
            {growth !== null && (
              <p className="mt-3 font-mono text-[10px] text-neutral-500" data-testid="revenue-growth">
                Period-on-period growth: <span className={growth >= 0 ? "text-emerald-300" : "text-rose-300"}>{growth >= 0 ? "+" : ""}{growth.toFixed(1)}%</span>
              </p>
            )}

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {FIELDS.map(([key, label]) => (
                <div key={key}>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{label}</label>
                  <input value={fig[key]} onChange={(e) => setFig({ ...fig, [key]: e.target.value.replace(/[^\d.-]/g, "") })}
                    inputMode="decimal" className={`${inputCls} mt-1.5 font-mono`} data-testid={`revenue-fig-${key}`} />
                </div>
              ))}
            </div>

            <div className="mt-5 flex flex-wrap gap-5 border-t border-white/10 pt-5">
              {toggle("methods", "Payment methods table", "revenue-include-methods")}
              {toggle("services", "Top services table", "revenue-include-services")}
              {toggle("customers", "Top customers table", "revenue-include-customers")}
            </div>

            <button onClick={generate} disabled={busy} data-testid="revenue-generate-btn"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-3.5 text-sm font-bold tracking-[0.12em] text-black disabled:opacity-40">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />}
              GENERATE REVENUE STATEMENT PDF
            </button>

            {result && (
              <div className="mt-4 rounded-xl border border-gold/30 bg-gold/[0.06] px-4 py-4" data-testid="revenue-result">
                <span className="font-mono text-xs font-bold text-goldlight" data-testid="revenue-doc-id">{result.doc_id}</span>
                <div className="mt-3 flex gap-2">
                  <a href={result.file_url} target="_blank" rel="noreferrer" data-testid="revenue-download-link"
                    className="flex-1 rounded-lg bg-gradient-to-br from-goldlight via-gold to-golddark py-2 text-center text-[11px] font-bold tracking-wide text-black transition-opacity hover:opacity-90">
                    DOWNLOAD PDF
                  </a>
                  <button type="button" data-testid="revenue-copy-link"
                    onClick={() => { navigator.clipboard.writeText(result.verify_url); toast.success("Verify link copied"); }}
                    className="flex-1 rounded-lg border border-gold/40 py-2 text-[11px] font-bold tracking-wide text-goldlight transition-colors hover:bg-gold/10">
                    COPY VERIFY LINK
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
}
